import clsx from "clsx";
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ExpressCheckoutElement, PaymentElement } from "@stripe/react-stripe-js";

import MigiriLogo from "../../components/svg/MigiriLogo";
import StripeLogo from "../../components/svg/StripeLogo";
import {
  stripeExpressCheckoutPaymentOptions,
  stripeExpressCheckoutTrialOptions,
  stripePaymentOptions
} from "../../lib/stripe/elementSetupOptions.tsx";
import { captureMessage } from "../../lib/sentry.ts";
import { getFormattedDueDate } from "../../lib/helpers/dateHelper.ts";
import { FeaturesList } from "../../components/FeaturesList/FeaturesList.tsx";
import { SubscriptionData } from "../../lib/mappers/subscriptionData";
import { StripePlanId } from "../../lib/types";
import { DEFAULT_PLAN, PLANS, PlanBilling } from "../../lib/plans";
import PlanPicker from "../../components/ABTesting/PlanPicker/PlanPicker";
import usePaymentAttempt from "../../hooks/usePaymentAttempt";
import { useWindowResizeListener } from "../../hooks/useWindowResizeListener.ts";

import FreeTrialCard from "./FreeTrialCard";
import { scrollIntoView } from "./handlers";
import PaymentCard from "./PaymentCard";
import StartTrialButton from "./StartTrialButton";
import "./FreeTrial.css";

const stripeExpressCheckoutOptions = (
  subscriptionData: SubscriptionData,
  plan?: PlanBilling
) => {
  if (subscriptionData.isPaymentRequired()) {
    return stripeExpressCheckoutPaymentOptions('white')
  } else {
    return stripeExpressCheckoutTrialOptions({
      subscriptionData: subscriptionData,
      trialStartDate: new Date(),
      theme: 'white',
      plan,
    })
  }
};

const FreeTrial = ({
  freeTrialActive,
  subscriptionData,
  multiplan = false,
}: {
  freeTrialActive: boolean;
  subscriptionData: SubscriptionData;
  multiplan?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [selectedPlanId, setSelectedPlanId] = useState<StripePlanId>(
    DEFAULT_PLAN.planId
  );
  const selectedPlan =
    PLANS.find((plan) => plan.planId === selectedPlanId) ?? DEFAULT_PLAN;
  const { setupAttemptHandler, paymentAttemptHandler, successReturnUrl } =
    usePaymentAttempt(multiplan ? selectedPlanId : undefined);
  const [expressCheckoutMounted, setExpressCheckoutMounted] = useState(false);
  const [expressCheckoutVisible, setExpressCheckoutVisible] = useState(false);
  const isDesktop = useWindowResizeListener()

  // Trial length + cancel-by date drive the multiplan trial copy (header +
  // wallet reassurance line). getDaysAmount() reads trial!.days, so it can only
  // be called when a trial exists — multiplan always renders trial-eligible.
  const trialDays = subscriptionData.isPaymentRequired()
    ? 0
    : subscriptionData.getDaysAmount();
  const trialDueDate = getFormattedDueDate(new Date(), trialDays);

  const submitHandler = subscriptionData.isPaymentRequired()
    ? paymentAttemptHandler
    : setupAttemptHandler;

  const stripeExpressCheckoutElementOptions = stripeExpressCheckoutOptions(
    subscriptionData,
    multiplan ? selectedPlan : undefined
  );
  const stripePaymentElementOptions = stripePaymentOptions;

  // The trial picker (multiplan), the single-plan trial card, or — for already
  // converted users — the payment summary. Rendered in the info column on
  // mobile and the payment column on desktop.
  const planSummary = subscriptionData.isPaymentRequired() ? (
    <PaymentCard paymentData={subscriptionData.getPaymentData()} />
  ) : multiplan ? (
    <div className="multiplanTrial">
      <div className="multiplanTrialHeader">
        <h3 className="semibold">Try Migiri Free for {trialDays} days</h3>
        <p className={clsx("body-md", "multiplanTrialSubtitle")}>
          Cancel anytime before {trialDueDate}.
        </p>
      </div>
      <PlanPicker selectedPlanId={selectedPlanId} onSelect={setSelectedPlanId} />
    </div>
  ) : (
    <FreeTrialCard
      freeDays={subscriptionData.getDaysAmount()}
      paymentData={subscriptionData.getPaymentData()}
      freeTrialActive={freeTrialActive}
    />
  );

  // Apple Pay / Google Pay freeze their recurringPaymentRequest at mount, so a
  // live options update does NOT change the amount/interval shown in the wallet
  // sheet. Re-key on the selected plan to force a remount (with the correct
  // recurring request) whenever the multiplan picker changes. Control keeps a
  // stable key (selectedPlanId never leaves the default), so it never remounts.
  const expressCheckoutElement = <ExpressCheckoutElement
    key={selectedPlanId}
    options={stripeExpressCheckoutElementOptions}
    onReady={(event) => {
      // If payment methods are available, set both states together
      if (event.availablePaymentMethods) {
        setExpressCheckoutMounted(true);
        setExpressCheckoutVisible(true);
      }
    }}
    onConfirm={async () => {
      const error = await submitHandler();
      if (error === undefined) {
        navigate(successReturnUrl);
      } else {
        captureMessage(`ExpressCheckout submitHandler ${error}`)
      }
    }}
  />

  // Reserve the wallet button's height + a button-coloured backdrop so the remount
  // above can't shift the layout or flash. Mobile nests this in the sticky
  // .paymentButtonWrapper; desktop renders it directly.
  const expressCheckoutSlot = (
    <div className={clsx("expressCheckoutSlot", expressCheckoutMounted && "reserved")}>
      {expressCheckoutElement}
    </div>
  );

  // Trailing wallet content: the "Pay $0.00…" trial reassurance (multiplan only —
  // control's FreeTrialCard already carries its own) plus the "or try with card"
  // divider. Both only apply once a wallet button has actually mounted.
  const walletFooter = expressCheckoutMounted ? (
    <div className="walletFooter">
      {multiplan && !subscriptionData.isPaymentRequired() && (
        <p className={clsx("walletTrialNote", "body-sm")}>
          <span className="semibold">Pay $0.00.</span> Cancel anytime before {trialDueDate}.
        </p>
      )}
      <div className="divider">or try with card</div>
    </div>
  ) : null;

  return (
    <>
      <div className="slanted-reflection-wrapper">
        <div className="slanted-reflection" />
      </div>

      <div className={clsx("FreeTrialPage", "free-trial-content", "content")}>
        <div className="contentWrapper">
          <div className="infoSection">
            <div className="migiriLogo">
              <MigiriLogo />
            </div>
            <h2 className="semibold">
              {freeTrialActive
                ? "Your first week is on us"
                : "Give yourself the chance to transform your life"}
            </h2>

            <FeaturesList />

            {!isDesktop && planSummary}

            {!isDesktop &&
              <div className={clsx("paymentButtonWrapper", expressCheckoutVisible && "visible")}>
                {expressCheckoutSlot}
              </div>
            }

            {!isDesktop && walletFooter}
          </div>

          <div className="paymentSection" ref={ref}>
            {isDesktop && planSummary}

            {isDesktop && expressCheckoutSlot}

            {isDesktop && walletFooter}

            <form onSubmit={submitHandler} style={{ overflow: "hidden" }}>
              <div className="formWrapper">
                <PaymentElement options={stripePaymentElementOptions} />

                <div className="startTrialWrapper">

                  <StartTrialButton
                    freeDays={trialDays}
                    freeTrialActive={freeTrialActive}
                    onClick={async (e) => {
                      e.preventDefault();
                      const error = await submitHandler();
                      if (error !== undefined) {
                        scrollIntoView(ref.current!);
                      } else {
                        navigate(successReturnUrl);
                      }
                    }}
                  />
                </div>

                <p className={clsx("securedByStripe", "caption-sm")} >
                  Secured by&nbsp;
                  <StripeLogo />
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default FreeTrial;